/**
 * Catálogo en español. **El de referencia**: `en.ts` tiene que tener sus
 * mismas claves o TypeScript no compila.
 *
 * Claves planas con puntos, no objetos anidados, y es una decisión:
 *
 *   · `t('pin.error_intentos')` está tipado, autocompleta y una clave que no
 *     existe es un error de compilación, no un texto en blanco en producción.
 *   · `grep pin.error_intentos` encuentra el texto y todos sus usos.
 *
 * Los textos salen del handoff palabra por palabra donde el handoff los da.
 * Donde no, se han escrito con el mismo tono: directo, sin jerga y sin
 * prometer nada que el sistema no pueda cumplir.
 *
 * Interpolación: `{nombre}` se sustituye por el valor que se pase.
 */

export const es = {
    // --- Comunes -------------------------------------------------------
    'comun.atras': 'Atrás',
    'comun.cerrar': 'Cerrar',
    'comun.reintentar': 'Reintentar',
    'comun.aceptar': 'Aceptar',
    'comun.cargando': 'Cargando…',
    'comun.de': 'de',
    'comun.sin_conexion': 'Sin conexión · todo guardado en el móvil',

    // --- 00 · Alta del dispositivo -------------------------------------
    'alta.etiqueta': 'PRIMER USO · UNA SOLA VEZ',
    'alta.titulo': 'Vincula tu móvil',
    'alta.intro':
        'Este enlace solo funciona una vez. Al vincular, el móvil recibe una credencial propia y ya no vuelves a necesitar el enlace.',
    'alta.paso_invitacion': 'Invitación verificada',
    'alta.paso_invitacion_sub': 'Enlace válido para {empleada}',
    'alta.paso_pin': 'Crea tu PIN de 6 dígitos',
    'alta.paso_pin_sub': 'Lo usarás cada día para empezar el turno',
    'alta.paso_instalar': 'Añade la app a tu pantalla de inicio',
    'alta.paso_instalar_ios':
        'Compartir → «Añadir a pantalla de inicio». Desde entonces entras por el icono, solo con tu PIN.',
    'alta.paso_instalar_android':
        'Menú → «Instalar app». Desde entonces entras por el icono, solo con tu PIN.',
    'alta.paso_instalar_otro':
        'Desde entonces entras por esta dirección con tu PIN. El enlace de la invitación ya no hace falta.',
    'alta.dispositivo': 'Este dispositivo',
    // Este bloque estaba escrito en español dentro del componente, así que a
    // quien tuviera el móvil en inglés le salía media pantalla en cada idioma
    // —y es la PRIMERA que ve—.
    'alta.dev_movil': 'móvil',
    'alta.dev_video': 'vídeo',
    'alta.dev_sin_video': 'no soportado',
    'alta.dev_segundo_plano': 'subida en segundo plano',
    'alta.dev_si': 'sí',
    'alta.dev_no': 'no',
    'alta.nota_revocacion':
        'Oficina puede revocar este móvil en cualquier momento. Si lo pierdes, avisa el mismo día.',
    'alta.boton': 'Vincular y crear PIN',
    'alta.sin_red': 'Sin conexión',
    'alta.sin_red_ayuda':
        'Para darte de alta hace falta conexión: este enlace se comprueba en el servidor. Conéctate al wifi o a los datos y vuelve a probar. Una vez dada de alta, la app funciona sin cobertura.',
    'alta.reintentar': 'Volver a probar',
    'alta.error_pin_existente':
        'Ya tienes un PIN. Para añadir este móvil escribe el PIN que usas, no uno nuevo.',
    'alta.error_bloqueada': 'Este enlace se ha bloqueado',
    'alta.error_bloqueada_ayuda':
        'Demasiados PIN fallidos. Pide otro enlace a oficina; si no recuerdas tu PIN, diles que te lo restablezcan.',
    'alta.sin_token': 'Este enlace no es válido',
    'alta.sin_token_ayuda':
        'Abre el enlace completo que te ha mandado oficina, con el código incluido. Si lo has copiado a mano, puede haberse cortado.',
    'alta.error_caducada': 'La invitación ha caducado',
    'alta.error_caducada_ayuda': 'Las invitaciones duran 24 horas. Pide otra a oficina.',
    'alta.error_usada': 'Esta invitación ya se ha usado',
    'alta.error_usada_ayuda':
        'Si has cambiado de móvil, oficina tiene que darte una invitación nueva.',
    'alta.paso_invitacion_sub_abierta': 'Enlace válido y sin usar',
    'alta.paso_datos': 'Escribe tu nombre y tus datos',
    'alta.paso_datos_sub': 'Solo el nombre es obligatorio',
    'alta.datos_titulo': 'Tus datos',
    'alta.datos_intro':
        'El nombre es el que verá oficina y el que va en cada vídeo que grabes. Escríbelo como quieras que te llamen.',
    'alta.campo_nombre': 'Tu nombre',
    'alta.campo_nombre_ph': 'Ej.: Marta G.',
    'alta.campo_telefono': 'Teléfono',
    'alta.campo_email': 'Correo',
    'alta.campo_idioma': 'Idioma de la app',
    'alta.opcional': 'opcional',
    'alta.falta_nombre': 'Escribe tu nombre para continuar',
    'alta.error_email': 'Ese correo no parece válido',
    'alta.error_email_repetido': 'Ese correo ya está dado de alta',
    'alta.continuar': 'Continuar',
    'alta.hola': 'Hola, {empleada}',

    // --- Creación y acceso con PIN -------------------------------------
    'pin.crear_titulo': 'Elige tu PIN',
    'pin.crear_intro': 'Seis dígitos. No lo compartas con nadie, ni con oficina.',
    'pin.repetir_titulo': 'Repite el PIN',
    'pin.no_coincide': 'Los dos PIN no coinciden',
    'pin.debil_iguales': 'No puede ser el mismo dígito seis veces',
    'pin.saludo': 'Hola de nuevo',
    'pin.subtitulo': '{empleada} · {dispositivo}. Introduce tu PIN de 6 dígitos para empezar el turno.',
    'pin.error': 'PIN incorrecto',
    'pin.error_intentos': 'PIN incorrecto · te queda{intentos:|n} {intentos} intento{intentos:|s}',
    'pin.bloqueado': 'Contacta con oficina para desbloquear',
    'pin.bloqueado_ayuda':
        'Este móvil se ha bloqueado tras 5 intentos. Solo oficina puede desbloquearlo.',
    'pin.olvidado': 'He olvidado mi PIN',
    'pin.olvidado_ayuda':
        'El PIN no se puede recuperar. Pídelo a oficina: te lo restablecen y eliges uno nuevo en este mismo móvil.',
    // Oficina le ha borrado el PIN. Se lo decimos, porque si no la pantalla
    // de elegir PIN aparece de la nada y parece que se ha perdido el alta.
    'pin.reiniciado_titulo': 'Elige un PIN nuevo',
    'pin.reiniciado_intro':
        'Oficina ha restablecido tu PIN. Elige uno nuevo de seis dígitos: solo lo sabrás tú.',
    'pin.reiniciado_ya': 'Tu PIN sigue activo. Introduce el que tenías.',
    'pin.borrar': 'Borrar',

    // --- 02 · Mis apartamentos de hoy ----------------------------------
    'hoy.saludo': 'Buenos días, {nombre}',
    'hoy.saludo_tarde': 'Buenas tardes, {nombre}',
    'hoy.progreso': '{hechas} de {total} completadas',
    'hoy.progreso_sub': '{pendientes} pendientes hoy · {subiendo} subiendo',
    'hoy.progreso_sub_sin_subida': '{pendientes} pendientes hoy',
    'hoy.grupo_pendientes': 'PENDIENTES',
    'hoy.grupo_completadas': 'COMPLETADAS',
    'hoy.grupo_libres': 'Libres',
    'hoy.grupo_libres_ayuda': 'Nadie los tiene asignados. Puedes cogerlos si te da tiempo.',
    'hoy.coger': 'Coger',
    'hoy.urgente': 'Urgente',
    'hoy.dormitorios': '{n} dorm.',
    'hoy.salida': 'salida {hora}',
    'hoy.empezar': 'Empezar',
    'hoy.continuar': 'Continuar',
    'hoy.completada': '{hora} · vídeo {duracion} subido',
    'hoy.completada_fotos': '{hora} · {fotos} fotos subidas',
    'hoy.subiendo': 'Subiendo {pct}%',
    'hoy.vacio': 'No tienes limpiezas asignadas hoy',
    'hoy.vacio_ayuda':
        'Si crees que es un error, avisa a oficina. También puedes escanear el QR de un apartamento.',
    'hoy.escanear': 'Escanear QR',
    'hoy.menu': 'Menú',
    'hoy.menu_cola': 'Cola de subida',
    'hoy.menu_idioma': 'Idioma',
    'hoy.menu_salir': 'Salir',

    // --- 03 · QR / NFC -------------------------------------------------
    'qr.titulo': 'Escanear QR del apartamento',
    'qr.ayuda': 'Apunta al código pegado junto a la puerta. Se abre solo al reconocerlo.',
    'qr.manual': 'Introducir código manualmente',
    'qr.manual_titulo': 'Código del apartamento',
    'qr.manual_ayuda': 'Los 32 caracteres que hay bajo el QR.',
    'qr.camara_sistema': 'Usa la cámara del móvil',
    'qr.camara_sistema_ayuda':
        'Abre la cámara normal del teléfono y apunta al código: el enlace abre esta app en el apartamento correcto. En este navegador no se puede escanear desde aquí dentro.',
    'qr.escaneando': 'Buscando el código…',
    'qr.sin_permiso': 'Sin acceso a la cámara',
    'qr.no_asignado': 'Este apartamento no está en tu lista de hoy',
    'qr.no_asignado_ayuda':
        '{apartamento} no te toca hoy. Si has ido igualmente, avisa a oficina antes de empezar.',

    // --- 04 · Recorrido por estancias ----------------------------------
    'recorrido.subtitulo_video': 'Recorrido guiado · {n} estancia{n:|s}',
    'recorrido.subtitulo_fotos': 'Fotos por estancia · {n} estancia{n:|s}',
    'recorrido.modo_video': 'Vídeo guiado',
    'recorrido.modo_video_sub': 'Clip corto por estancia',
    'recorrido.modo_fotos': 'Fotos',
    'recorrido.modo_fotos_sub': 'Checklist por estancia',
    'recorrido.siguiente_etiqueta': 'Siguiente',
    'recorrido.grabar': 'Grabar {estancia}',
    'recorrido.fotografiar': 'Fotos de {estancia}',
    'recorrido.terminar': 'Ir al resumen',
    'recorrido.no_accesible': 'Marcar como no accesible',
    'recorrido.no_accesible_titulo': 'No se ha podido acceder',
    'recorrido.no_accesible_ayuda':
        'Dinos por qué no has podido entrar. Queda registrado con la limpieza.',
    'recorrido.no_accesible_ejemplo': 'Ej.: el huésped seguía dentro',
    'recorrido.sin_estancias': 'Este apartamento no tiene estancias configuradas',
    'recorrido.sin_estancias_ayuda':
        'Sin estancias no se puede dejar prueba de la limpieza. Avisa a oficina para que las configure; mientras tanto puedes cerrar la visita, pero quedará sin grabar.',

    // --- 05 · Grabando -------------------------------------------------
    'grabar.contexto': '{estancia} · {n}/{total}',
    'grabar.guia_titulo': 'Enfoca en este orden',
    'grabar.empezar': 'Empezar a grabar',
    'grabar.parar': 'Parar',
    'grabar.repetir': 'Repetir toma',
    'grabar.linterna': 'Linterna',
    'grabar.corto': 'Muy corto: mínimo {segundos} segundos',
    'grabar.aviso_largo': 'Quedan {segundos} s',
    'grabar.sin_camara': 'Sin acceso a la cámara',
    'grabar.sin_camara_ios':
        'En iPhone: Ajustes → Safari → Cámara → Permitir. Después vuelve a abrir la app desde el icono.',
    'grabar.sin_camara_android':
        'Toca el candado de la barra de direcciones → Permisos → Cámara → Permitir.',
    'grabar.sin_camara_pwa':
        'El permiso se pide por cada sitio. Si has instalado la app en la pantalla de inicio, vuelve a darlo desde aquí.',

    // --- 06 · Fotos por checklist --------------------------------------
    'fotos.titulo': '{estancia} · fotos',
    'fotos.instruccion': 'Haz una foto de cada punto. Mínimo {n} para poder cerrar la estancia.',
    'fotos.hacer': 'Hacer foto',
    'fotos.rehacer': 'Repetir',
    'fotos.faltan': 'Falta{n:|n} {n} foto{n:|s}',
    'fotos.aviso_incidencia': '¿Algo roto o manchado? Añade una incidencia y avisamos a oficina.',
    'fotos.anadir_incidencia': 'Añadir incidencia',
    'fotos.siguiente_estancia': 'Siguiente estancia',

    // --- Incidencias ---------------------------------------------------
    'incidencia.titulo': 'Nueva incidencia',
    'incidencia.texto': 'Qué has visto (obligatorio)',
    'incidencia.texto_ejemplo': 'Ej.: la mampara de la ducha está rajada',
    'incidencia.gravedad': 'Gravedad',
    'incidencia.gravedad_baja': 'Leve',
    'incidencia.gravedad_media': 'Normal',
    'incidencia.gravedad_alta': 'Urgente',
    'incidencia.guardar': 'Guardar incidencia',
    'incidencia.guardada': 'Incidencia registrada · oficina la verá',

    // --- 07 · Resumen y cierre -----------------------------------------
    'resumen.titulo': 'Limpieza registrada',
    'resumen.subtitulo': '{apartamento} · {fecha}, {hora} · {duracion}',
    'resumen.estancias': 'Estancias',
    'resumen.videos': 'Vídeos',
    'resumen.fotos': 'Fotos',
    'resumen.tamano': 'Tamaño',
    'resumen.incidencias': 'Incidencias',
    'resumen.sello': 'Sello de la prueba',
    'resumen.sello_gps': 'GPS {lat}, {lng} · ±{precision} m',
    'resumen.sello_sin_gps': 'Sin GPS: el móvil no ha dado la posición',
    'resumen.sello_dispositivo': '{dispositivo} · app {version}',
    'resumen.nota': 'Nota para oficina (opcional)',
    'resumen.nota_ejemplo': 'Ej.: la terraza estaba con arena, he tardado más',
    'resumen.finalizar': 'Finalizar y subir',
    'resumen.pendientes': 'Te queda{n:|n} {n} estancia{n:|s} sin cerrar',
    'resumen.pendientes_ayuda':
        'Ciérralas o márcalas como no accesibles con su motivo. No se puede finalizar con estancias pendientes.',

    // Este texto CAMBIA según el móvil: en iPhone no se puede prometer que
    // suba con la app cerrada, porque Safari no lo permite. Ver D3.
    'resumen.subida_fondo': 'Se subirá en segundo plano aunque cierres la app',
    'resumen.subida_primer_plano': 'Deja la app abierta hasta que acabe de subir',
    'resumen.subida_wifi': 'Esperando WiFi para subir',

    // --- 08 · Cola de subida -------------------------------------------
    'cola.titulo': 'Cola de subida',
    'cola.resumen': '{n} limpieza{n:|s} esperando · {tamano}',
    'cola.resumen_vacia': 'Todo subido. No queda nada en el móvil.',
    'cola.subiendo': '{pct}% · {enviado}/{total}',
    'cola.reanudable': 'Reanudable · trozos de 5 MB',
    'cola.en_cola': 'En cola',
    'cola.fallida': 'Falló {n} ve{n:z|ces}',
    'cola.reintentar_ahora': 'Reintentar ahora',
    'cola.esperando_red': 'Esperando conexión',
    'cola.esperando_wifi': 'Esperando WiFi',
    'cola.solo_wifi': 'Subir solo con WiFi',
    'cola.almacenamiento': 'almacenamiento local {usado} / límite del dispositivo {limite}',
    'cola.almacenamiento_aviso':
        'Queda poco espacio en el móvil. Conéctate a WiFi para vaciar la cola antes de seguir grabando.',
    'cola.pantalla_encendida': 'Mantén la pantalla encendida mientras suba',

    // --- Errores de red y sesión ---------------------------------------
    'error.sin_red': 'Sin conexión',
    'error.sin_red_ayuda': 'Puedes seguir trabajando: todo se guarda en el móvil y se sube al volver la red.',
    'error.dispositivo_revocado': 'Este móvil ya no está autorizado',
    'error.inesperado': 'Algo ha ido mal',
    // El aviso de protección de datos, en el alta (0024).
    //
    // `rgpd_` y no `datos_` porque `alta.datos_titulo` YA EXISTE: es el rótulo
    // del formulario donde ella escribe su nombre. Una clave repetida en un
    // objeto se pisa en silencio, y la que gana es la de abajo.
    //
    // Es distinto del aviso del huésped y tiene que serlo: a él se le habla de
    // los vídeos de SU apartamento, y aquí a ella de SUS datos —el nombre que
    // va en cada fichero, el teléfono, el correo y el móvil—. Y se tutea, como
    // el resto de esta app.
    //
    // Sale en la pantalla en la que los escribe, no después: decírselo cuando
    // ya los ha entregado es decírselo tarde.
    'alta.rgpd_titulo': 'Protección de datos',
    'alta.rgpd_que':
        'Se guardan tu nombre, el móvil que vinculas y, si los escribes, tu teléfono y tu correo. Tu nombre va en el fichero de cada vídeo que grabes y lo ve la oficina.',
    'alta.rgpd_plazo': 'Las limpiezas se guardan {dias} día{dias:|s}.',
    'alta.rgpd_plazo_indefinido': 'Las limpiezas se guardan sin un plazo fijado.',
    'alta.rgpd_anonimiza': 'Al cumplirse ese plazo también se borra quién las hizo.',
    'alta.rgpd_responsable': 'Responsable',
    'alta.rgpd_derechos':
        'Puedes pedir acceso a tus datos, que se corrijan o que se borren escribiendo a {contacto}.',
    'alta.rgpd_sin_configurar':
        'Esta instalación todavía no tiene configurados los datos de contacto del responsable. Pídeselos a la oficina.',
} as const;
